import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Common";
import MatchScoreCard from "@/components/shared/MatchScoreCard";
import DocumentViewer from "@/components/shared/DocumentViewer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ADMIN } from "@/constants/testIds";
import { ShieldCheck, X, Check, Eye, EyeOff, Sliders, Languages } from "lucide-react";

function EnglishVerifyControl({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const verified = !!user.english_verified;
  const hasCert = !!user.english_certificate_doc_id;
  const requested = !!user.english_assessment_requested;

  const setVerified = async (v) => {
    setBusy(true);
    try {
      await api.post(`/admin/staff/${user.id}/verify-english`, { verified: v });
      toast.success(v ? "English marked as verified" : "English verification removed");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const level = user.english_level || "—";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`admin-english-block-${user.secusource_id}`}>
      <span className={`pill ${verified ? "pill-approved" : hasCert || requested ? "pill-pending" : "pill-rejected"}`} data-testid={`admin-english-status-${user.secusource_id}`}>
        <Languages className="w-3 h-3 mr-1 inline" /> {verified ? `English ✓ ${level}` : hasCert ? "Cert ready" : requested ? "Assessment req." : `English ${level}`}
      </span>
      {verified ? (
        <Button size="sm" variant="outline" onClick={() => setVerified(false)} disabled={busy} className="border-white/15 text-white/70 h-7 px-2" data-testid={`admin-english-unverify-${user.secusource_id}`}>
          Unverify
        </Button>
      ) : (
        <Button size="sm" onClick={() => setVerified(true)} disabled={busy} className="bg-gold-500 hover:bg-gold-600 text-white h-7 px-2" data-testid={`admin-english-verify-${user.secusource_id}`}>
          <Check className="w-3.5 h-3.5 mr-1" /> Verify English
        </Button>
      )}
    </div>
  );
}

function ScoreControls({ user, onChange }) {
  const [open, setOpen] = useState(false);
  const [delta, setDelta] = useState(user.score_override_delta || 0);
  const [role, setRole] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBreakdown = async () => {
    try {
      const params = role ? `?role=${encodeURIComponent(role)}` : "";
      const { data } = await api.get(`/admin/users/${user.id}/score-breakdown${params}`);
      setBreakdown(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyOverride = async () => {
    if (delta < -20 || delta > 20) return toast.error("Delta must be between -20 and +20");
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/score-override`, { delta: Number(delta) });
      toast.success("Score override applied");
      onChange && onChange();
      await loadBreakdown();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleHidden = async () => {
    try {
      await api.post(`/admin/users/${user.id}/toggle-hidden`, { hidden: !user.hidden });
      toast.success(user.hidden ? "Profile visible" : "Profile hidden from search");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) loadBreakdown(); }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="border-white/15" data-testid={`admin-score-btn-${user.secusource_id}`}>
            <Sliders className="w-3.5 h-3.5 mr-1" /> Score
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Score for {user.secusource_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Preview against role (optional)</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g., CPO / VPO" />
              <Button size="sm" onClick={loadBreakdown} className="mt-2 bg-slate-800 hover:bg-navy-900 text-white">Recalculate</Button>
            </div>
            {breakdown && <MatchScoreCard match={breakdown} size="md" showBreakdown={true} />}
            <div className="border-t pt-3">
              <Label>Manual override (−20 to +20 points)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min={-20} max={20} step={0.5} value={delta} onChange={(e) => setDelta(e.target.value)} data-testid={`admin-score-delta-${user.secusource_id}`} />
                <Button onClick={applyOverride} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid={`admin-score-apply-${user.secusource_id}`}>Apply</Button>
              </div>
              <div className="text-xs text-white/60 mt-1 font-manrope">Applied on top of the calculated score. Final score is capped 0–100.</div>
            </div>
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>

      <Button
        variant="outline" size="sm"
        onClick={toggleHidden}
        className={`border-white/15 ${user.hidden ? "text-red-600" : ""}`}
        data-testid={`admin-hide-btn-${user.secusource_id}`}
      >
        {user.hidden
          ? <><Eye className="w-3.5 h-3.5 mr-1" /> Unhide</>
          : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</>}
      </Button>
    </div>
  );
}

export default function AdminStaff() {
  const [all, setAll] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");   // all | guard | cpo | driver | other

  const load = async () => {
    try { const { data } = await api.get("/admin/all-staff"); setAll(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (uid, approved) => {
    try {
      await api.post(`/admin/approve-user/${uid}`, { approved });
      toast.success(approved ? "Approved" : "Rejected");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Classify each user by primary role category so we never mix CPOs into a Security Guard list.
  const classifyRole = (u) => {
    const cats = (u.role_categories || []).map((c) => (c || "").toLowerCase());
    if (cats.some((c) => c.includes("cpo") || c.includes("vpo") || c.includes("close protection"))) return "cpo";
    if (cats.some((c) => c.includes("driver"))) return "driver";
    if (cats.some((c) => c.includes("security guard") || c.includes("event security") || c.includes("static") || c.includes("door") || c.includes("steward"))) return "guard";
    return "other";
  };

  const inRoleFilter = (u) => roleFilter === "all" ? true : classifyRole(u) === roleFilter;

  const pending  = all.filter((u) => u.status === "pending"  && inRoleFilter(u));
  const approved = all.filter((u) => u.status === "approved" && inRoleFilter(u));
  const rejected = all.filter((u) => u.status === "rejected" && inRoleFilter(u));

  const roleCounts = {
    all:    all.length,
    guard:  all.filter((u) => classifyRole(u) === "guard").length,
    cpo:    all.filter((u) => classifyRole(u) === "cpo").length,
    driver: all.filter((u) => classifyRole(u) === "driver").length,
    other:  all.filter((u) => classifyRole(u) === "other").length,
  };

  const ROLE_TABS = [
    { key: "all",    label: "All",              accent: "brand" },
    { key: "guard",  label: "Security Guards",  accent: "brand" },
    { key: "cpo",    label: "CPOs / VPOs",      accent: "violet" },
    { key: "driver", label: "Security Drivers", accent: "cyan" },
    { key: "other",  label: "Other",            accent: "slate" },
  ];

  const Row = ({ u }) => (
    <div className="card-flat p-4 flex flex-wrap gap-3 justify-between items-start" data-testid={`staff-row-${u.secusource_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="ss-id">{u.secusource_id}</span>
          <StatusPill status={u.status} />
          {u.hidden && <span className="pill pill-rejected">Hidden</span>}
          {typeof u.score_override_delta === "number" && u.score_override_delta !== 0 && (
            <span className="pill pill-pending">Override {u.score_override_delta > 0 ? "+" : ""}{u.score_override_delta}</span>
          )}
        </div>
        {/* Runtime status badges — vetting / on job / in team / in deployment */}
        <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid={`staff-state-${u.secusource_id}`}>
          {u.vetting_state === "fully_vetted" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-500 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
              <Check className="w-3 h-3" strokeWidth={3} /> Fully vetted
            </span>
          )}
          {u.vetting_state === "provisionally_vetted" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
              <ShieldCheck className="w-3 h-3" /> Provisionally vetted
            </span>
          )}
          {u.in_deployment && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> In deployment
            </span>
          )}
          {!u.in_deployment && u.on_job && (
            <span className="inline-flex items-center gap-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> On a job
            </span>
          )}
          {u.in_team && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
              <ShieldCheck className="w-3 h-3" /> In a team
            </span>
          )}
        </div>
        <div className="font-outfit font-semibold text-white mt-1">{u.full_name}</div>
        <div className="text-sm text-white/60 font-manrope">{u.county} · {u.years_experience} yr exp · {(u.role_categories || []).join(", ")}</div>
        <div className="text-xs text-white/50 font-mono">{u.email} · {u.phone}</div>
      </div>
      <div className="flex gap-2 flex-wrap justify-end items-start">
        {u.status === "pending" && (
          <>
            <DocumentViewer user={u} />
            <EnglishVerifyControl user={u} onChange={load} />
            <Button onClick={() => decide(u.id, true)} className="bg-gold-500 hover:bg-gold-600 text-white" data-testid={`${ADMIN.approveBtn}-${u.secusource_id}`}>
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button onClick={() => decide(u.id, false)} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" data-testid={`${ADMIN.rejectBtn}-${u.secusource_id}`}>
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
          </>
        )}
        {u.status === "approved" && (
          <>
            <DocumentViewer user={u} />
            <EnglishVerifyControl user={u} onChange={load} />
            <ScoreControls user={u} onChange={load} />
            <Link to={`/admin/cpo-vetting/${u.id}`} data-testid={`admin-cpo-vetting-btn-${u.secusource_id}`}>
              <Button variant="outline" className="border-violet-400 text-violet-300 hover:bg-violet-500/10">
                <ShieldCheck className="w-4 h-4 mr-1" /> CPO Vetting
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-brand" /> Security Professionals</h1>

      {/* Role separator — CPOs are NEVER mixed with Security Guards */}
      <div className="flex flex-wrap gap-2" data-testid="admin-role-tabs">
        {ROLE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setRoleFilter(t.key)}
            data-testid={`admin-role-tab-${t.key}`}
            className={`px-4 py-2 rounded-md font-outfit font-semibold text-sm border transition ${
              roleFilter === t.key
                ? "bg-brand text-black border-brand"
                : "bg-[#0A0A0A] text-white/85 border-white/10 hover:border-brand"
            }`}
          >
            {t.label} <span className={`ml-1 text-[10px] ${roleFilter === t.key ? "text-black/70" : "text-white/50"}`}>({roleCounts[t.key] || 0})</span>
          </button>
        ))}
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending-staff">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-staff">Approved ({approved.length})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected-staff">Rejected ({rejected.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-3 mt-4">
          {pending.length === 0 && <div className="text-white/60 font-manrope">Nothing pending. Excellent.</div>}
          {pending.map((u) => <Row key={u.id} u={u} />)}
        </TabsContent>
        <TabsContent value="approved" className="space-y-3 mt-4">
          {approved.length === 0 && <div className="text-white/60 font-manrope">No approved staff yet.</div>}
          {approved.map((u) => <Row key={u.id} u={u} />)}
        </TabsContent>
        <TabsContent value="rejected" className="space-y-3 mt-4">
          {rejected.length === 0 && <div className="text-white/60 font-manrope">No rejected staff.</div>}
          {rejected.map((u) => <Row key={u.id} u={u} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
